import { Component, ojs, app, state, v, payload } from "modular-openscriptjs";
import Swal from "sweetalert2";
import { dom, tool } from "../../fotastart";
import { cleanDispose, isMobile, eParse } from "../../helpers";
import { api, dc } from "../../declarations";
import { uc } from "../../ojs.contexts";

const h = app("h");
const broker = app("broker");
const $e = app("$e");

export default class ProductExtractionProgressForm extends Component {
    holder = null;
    statusText = state("Analyzing request...");
    pendingApprovals = state([]);
    loading = state(false);
    isCompleted = state(false);
    isFailed = state(false);
    activeTab = state("logs"); // "logs" or "approvals"
    conversationId = null;

    constructor() {
        super();
        this.statusText.listener(this);
        this.pendingApprovals.listener(this);
        this.loading.listener(this);
        this.isCompleted.listener(this);
        this.isFailed.listener(this);
        this.activeTab.listener(this);
    }

    render(...args) {
        return h.empty();
    }

    async showForm(ed, event) {
        let data = eParse(ed);
        const prompt = data?.message.get("prompt", "");
        const mode = data?.message.get("mode", null);
        const hasImages = mode === "images";

        // If the form is already open, do not restart unless it's a new request
        if (this.holder && this.holder.isShown?.() && !prompt && !hasImages) {
            return;
        }

        // Setup states for a new request
        if (prompt || hasImages) {
            this.statusText.value = hasImages
                ? "Analyzing your pictures..."
                : "Analyzing shopping list...";
            this.pendingApprovals.value = [];
            this.loading.value = true;
            this.isCompleted.value = false;
            this.isFailed.value = false;
            this.activeTab.value = "logs";
            this.conversationId = null;
        }

        this.openUI();

        if (prompt) {
            this.submitPrompt(prompt);
        } else if (hasImages) {
            const images = uc.pendingShoppingImages.value || [];
            this.submitPrompt(null, null, images);
        }
    }

    openUI() {
        if(this.holder && this.holder?._isShown) {
            return;
        }

        setTimeout(() => {
            if (isMobile()) {
                this.holder = tool.offcanvas(
                    [
                        h.ProgressCanvas(this.renderForm()),
                        h.div(
                            {
                                class: "offcanvas-footer p-3 border-0 bg-transparent",
                                style: "flex-shrink: 0;",
                            },
                            this.renderFooter(),
                        ),
                    ],
                    { backdrop: true },
                );
            } else {
                this.holder = tool.plainModal(
                    h.ProgressModal(this.renderForm(), this.renderFooter()),
                    { backdrop: true },
                );
            }

            this.holder.show();
        }, 1000);
    }

    closeForm() {
        if (this.holder) {
            this.holder.hide();
        }
    }

    async submitPrompt(prompt, decisions = null, images = null) {
        const url = `${api.baseUrl}/user/add-items`;
        const hasImages = Array.isArray(images) && images.length > 0;

        this.statusText.value = decisions
            ? "Updating list..."
            : hasImages
                ? "Analyzing your pictures..."
                : "Analyzing shopping list...";
        this.loading.value = true;

        try {
            const headers = {
                Accept: "application/json",
                Authorization: `Bearer ${api.token}`,
                "X-Socket-ID": window.Echo ? window.Echo.socketId() : "",
            };

            let fetchOptions;

            if (hasImages) {
                const formData = new FormData();

                if (prompt) formData.append("prompt", prompt);

                images.forEach((img, idx) => {
                    formData.append(
                        "images[]",
                        img.file,
                        img.name || `photo-${idx}.jpg`,
                    );
                });

                // Do not set Content-Type manually — the browser needs to add
                // the multipart boundary itself.
                fetchOptions = { method: "POST", headers, body: formData };
            } else {
                const body = decisions
                    ? { decisions, conversation_id: this.conversationId }
                    : { prompt };

                fetchOptions = {
                    method: "POST",
                    headers: { ...headers, "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                };
            }

            const response = await fetch(url, fetchOptions);

            if (!response.ok) {
                this.loading.value = false;
                this.isFailed.value = true;

                if (response.status === 402) {
                    try {
                        const data = await response.json();
                        const creditsNeeded = data.credits_required;
                        const balance = data.balance_available;
                        this.closeForm();

                        Swal.fire({
                            title: "Insufficient AI Credits",
                            html: `This operation requires at least <strong>${creditsNeeded} AI Credits</strong>, but your current balance is <strong>${balance} Credits</strong>.<br><br>Would you like to buy credits now?`,
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonText: "Buy AI Credits",
                            cancelButtonText: "Cancel",
                            confirmButtonColor: "#6366f1",
                            cancelButtonColor: "#64748b",
                            customClass: {
                                popup: "rounded-4 border-0",
                            },
                        }).then((result) => {
                            if (result.isConfirmed) {
                                broker.send($e.aiCredit.needs.depositForm);
                            }
                        });
                        return;
                    } catch (err) {}
                }

                this.statusText.value =
                    "Failed to extract items. Please try again.";
                // Auto reopen to show error status if closed
                this.openUI();
                return;
            }

            const data = await response.json();
            this.conversationId = data.conversation_id;

            if (data.status === "awaiting_approval") {
                this.statusText.value =
                    "Review required: Please verify extracted items.";
                this.pendingApprovals.value = data.approvals.map(
                    (approval) => ({
                        ...approval,
                        editedArguments: { ...approval.arguments },
                        action: "approve", // default action
                    }),
                );
                this.activeTab.value = "approvals";
                this.loading.value = false;
                this.isFailed.value = false;
                // Pop the UI open to let the user approve items
                this.openUI();
            } else if (data.status === "complete") {
                this.isCompleted.value = true;
                this.loading.value = false;
                this.isFailed.value = false;
                uc.pendingShoppingList.value = "";
                this.clearPendingImages();
                this.statusText.value = "Items added to cart successfully!";
                broker.send($e.cart.needs.allItems);
                // Pop the UI open to show success
                this.openUI();
            }
        } catch (error) {
            this.statusText.value =
                "Connection error. Please check your internet connection.";
            this.loading.value = false;
            this.isFailed.value = true;
            this.openUI();
        }
    }

    clearPendingImages() {
        const images = uc.pendingShoppingImages.value || [];
        for (const img of images) {
            if (img?.url) URL.revokeObjectURL(img.url);
        }
        uc.pendingShoppingImages.value = [];
    }

    submitDecisions() {
        const decisionsData = {};
        for (const approval of this.pendingApprovals.value) {
            if (approval.action === "approve") {
                decisionsData[approval.id] = {
                    action: "edit",
                    arguments: approval.editedArguments,
                };
            } else {
                decisionsData[approval.id] = {
                    action: "reject",
                    result: "the user does not need this product anymore",
                };
            }
        }

        this.pendingApprovals.value = [];
        this.activeTab.value = "logs";
        this.submitPrompt(null, decisionsData);
    }

    updateApprovalField(approvalId, field, value) {
        this.pendingApprovals.value = this.pendingApprovals.value.map(
            (appr) => {
                if (appr.id === approvalId) {
                    return {
                        ...appr,
                        editedArguments: {
                            ...appr.editedArguments,
                            [field]: value,
                        },
                    };
                }
                return appr;
            },
        );
    }

    toggleApprovalAction(approvalId, action) {
        this.pendingApprovals.value = this.pendingApprovals.value.map(
            (appr) => {
                if (appr.id === approvalId) {
                    return { ...appr, action };
                }
                return appr;
            },
        );
    }

    renderForm() {
        // Inject keyframe style block for typing wave
        const styleBlock = h.style(`
            @keyframes typingWave {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-8px); }
            }
            .typing-wave-container {
                display: flex;
                gap: 6px;
                justify-content: center;
                align-items: center;
                height: 40px;
            }
            .typing-wave-dot {
                width: 10px;
                height: 10px;
                background-color: #6366f1;
                border-radius: 50%;
                animation: typingWave 1.2s infinite ease-in-out;
            }
            .typing-wave-dot:nth-child(1) { animation-delay: 0s; }
            .typing-wave-dot:nth-child(2) { animation-delay: 0.15s; }
            .typing-wave-dot:nth-child(3) { animation-delay: 0.3s; }
        `);

        return h.div(
            {
                class: "w-100 text-start d-flex flex-column h-100",
                style: "min-height: 380px;",
            },
            styleBlock,

            // Header
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4 flex-shrink-0",
                },
                h.div(
                    { class: "d-flex align-items-center gap-3" },
                    h.div(
                        {
                            class: "d-flex align-items-center justify-content-center rounded-circle flex-shrink-0",
                            style: [
                                "width: 42px; height: 42px;",
                                "background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);",
                                "box-shadow: 0 4px 15px rgba(99, 102, 241, 0.25);",
                            ].join(" "),
                        },
                        h.i({
                            class: "bi bi-stars text-white",
                            style: "font-size: 16px;",
                        }),
                    ),
                    h.div(
                        h.p(
                            {
                                class: "fw-bold mb-0",
                                style: "font-size: 16px; color: #1e1b4b;",
                            },
                            "Carata AI",
                        ),
                        h.p(
                            { class: "text-muted mb-0 small" },
                            "Extracting products directly into your cart",
                        ),
                    ),
                ),
                h.button({
                    class: "btn-close bg-light rounded-circle p-2 border-0",
                    onclick: this.method("closeForm"),
                    aria_label: "Close",
                }),
            ),

            // Tab contents / Active workspace
            h.div(
                {
                    class: "flex-grow-1 overflow-hidden d-flex flex-column justify-content-center align-items-stretch py-2 w-100",
                },
                v(this.activeTab, (tab) => {
                    if (tab.value === "approvals") {
                        return this.renderApprovalsTab();
                    }
                    return this.renderProgressState();
                }),
            ),
        );
    }

    renderProgressState() {
        return h.div(
            {
                class: "text-center py-5 px-3 w-100 d-flex flex-column align-items-center justify-content-center gap-4",
            },

            // Pulsing checkmark or Typing Wave animation
            v(this.isCompleted, (isDone) => {
                if (isDone.value) {
                    return h.div(
                        {
                            class: "d-flex align-items-center justify-content-center rounded-circle animate__animated animate__zoomIn",
                            style: "width: 72px; height: 72px; background: #e0f2fe; color: #0284c7; font-size: 32px;",
                        },
                        h.i({ class: "bi bi-check-lg" }),
                    );
                }

                // Smooth typing bouncing wave
                return h.div(
                    { class: "typing-wave-container" },
                    h.div({ class: "typing-wave-dot" }),
                    h.div({ class: "typing-wave-dot" }),
                    h.div({ class: "typing-wave-dot" }),
                );
            }),

            // Status Text
            h.div(
                { class: "d-flex flex-column gap-2" },
                v(this.statusText, (status) =>
                    h.p(
                        {
                            class: "fw-semibold mb-0 fs-5 animate__animated animate__fadeIn",
                            style: "color: #1e293b;",
                        },
                        status.value,
                    ),
                ),
                v(this.loading, (isLoading) =>
                    isLoading.value
                        ? h.p(
                              { class: "text-muted small mb-0" },
                              "I may take up to one (1) minute. You can close this, I will open it when I need your attention.",
                          )
                        : h.empty(),
                ),
            ),
        );
    }

    renderApprovalsTab() {
        return h.div(
            {
                class: "w-100 d-flex flex-column gap-3 h-100 align-items-stretch",
            },

            // Instruction title
            h.div(
                { class: "px-1" },
                h.h6(
                    { class: "fw-bold mb-1", style: "color: #1e293b;" },
                    "Extracted Items",
                ),
                h.p(
                    { class: "text-muted small mb-0" },
                    "Please verify and adjust prices or descriptions before saving to your cart.",
                ),
            ),

            // Horizontal cards list
            h.div(
                {
                    class: "w-100 overflow-x-auto py-2 px-1",
                    style: "white-space: nowrap; -webkit-overflow-scrolling: touch; scroll-snap-type: x mandatory; display: block;",
                },
                v(this.pendingApprovals, (approvals) =>
                    h.div(
                        { class: "d-inline-flex gap-3" },
                        approvals.value.map((approval) => {
                            const isApproved = approval.action === "approve";

                            return h.div(
                                {
                                    class: "card text-start d-inline-block",
                                    style: [
                                        "width: 290px; vertical-align: top; white-space: normal; scroll-snap-align: start; transition: all 0.2s ease;",
                                        "background: #ffffff; border-radius: 18px;",
                                        "border: 1px solid #e2e8f0 !important;",
                                        isApproved ? "" : "opacity: 0.65;",
                                    ].join(" "),
                                },
                                h.div(
                                    {
                                        class: "card-body p-3 d-flex flex-column gap-3",
                                    },

                                    // Pill toggle selectors
                                    h.div(
                                        {
                                            class: "d-flex gap-1 p-1 rounded-pill",
                                            style: "background: #f1f5f9;",
                                        },
                                        h.button(
                                            {
                                                class: `btn btn-sm rounded-pill flex-fill py-1.5 fw-bold border-0 ${isApproved ? "bg-dark text-white shadow-sm" : "text-muted bg-transparent"}`,
                                                listeners: {
                                                    click: () =>
                                                        this.toggleApprovalAction(
                                                            approval.id,
                                                            "approve",
                                                        ),
                                                },
                                            },
                                            h.i({ class: "bi bi-check2 me-1" }),
                                            "Keep",
                                        ),
                                        h.button(
                                            {
                                                class: `btn btn-sm rounded-pill flex-fill py-1.5 fw-bold border-0 ${!isApproved ? "bg-dark text-white shadow-sm" : "text-muted bg-transparent"}`,
                                                listeners: {
                                                    click: () =>
                                                        this.toggleApprovalAction(
                                                            approval.id,
                                                            "reject",
                                                        ),
                                                },
                                            },
                                            h.i({ class: "bi bi-x me-1" }),
                                            "Skip",
                                        ),
                                    ),

                                    // Product Details Info Panel
                                    approval.product_details ? h.div(
                                        {
                                            class: "p-2 rounded-3 border-0",
                                            style: "background-color: #f8fafc; border: 1px solid #f1f5f9 !important;"
                                        },
                                        h.div(
                                            { class: "fw-bold small text-dark mb-1", style: "font-size: 12px;" },
                                            approval.product_details.name || "Unnamed Product"
                                        ),
                                        approval.product_details.description ? h.div(
                                            { class: "text-muted mb-1.5", style: "font-size: 11px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.4;" },
                                            approval.product_details.description
                                        ) : h.empty(),
                                        h.div(
                                            { class: "badge bg-secondary-subtle text-secondary small fw-bold px-2 py-1", style: "font-size: 10px;" },
                                            `KES ${approval.product_details.price || 0}`
                                        )
                                    ) : h.empty(),

                                    // Fields editor
                                    isApproved
                                        ? h.div(
                                              {
                                                  class: "d-flex flex-column gap-2",
                                              },
                                              h.div(
                                                  h.label(
                                                      {
                                                          class: "small fw-semibold mb-1",
                                                          style: "color: #64748b;",
                                                      },
                                                      "Description (Okay?)",
                                                  ),
                                                  h.input({
                                                      type: "text",
                                                      class: "form-control form-control-sm rounded-3 border-0 bg-light px-3 py-2",
                                                      style: "font-size: 13px;",
                                                      value: approval
                                                          .editedArguments.name,
                                                      listeners: {
                                                          input: (e) =>
                                                              this.updateApprovalField(
                                                                  approval.id,
                                                                  "name",
                                                                  e.target
                                                                      .value,
                                                              ),
                                                      },
                                                  }),
                                              ),
                                              h.div(
                                                  h.label(
                                                      {
                                                          class: "small fw-semibold mb-1",
                                                          style: "color: #64748b;",
                                                      },
                                                      `Estimated Price (${dc.userCurrency}) (Okay?)`,
                                                  ),
                                                  h.input({
                                                      type: "number",
                                                      class: "form-control form-control-sm rounded-3 border-0 bg-light px-3 py-2",
                                                      style: "font-size: 13px;",
                                                      value:
                                                          approval
                                                              .editedArguments
                                                              .selling_price ||
                                                          0,
                                                      listeners: {
                                                          input: (e) =>
                                                              this.updateApprovalField(
                                                                  approval.id,
                                                                  "selling_price",
                                                                  parseFloat(
                                                                      e.target
                                                                          .value,
                                                                  ) || 0,
                                                              ),
                                                      },
                                                  }),
                                              ),
                                          )
                                        : h.div(
                                              {
                                                  class: "text-center text-muted py-4 d-flex flex-column align-items-center justify-content-center gap-2",
                                              },
                                              h.div(
                                                  {
                                                      class: "rounded-circle d-flex align-items-center justify-content-center",
                                                      style: "width: 42px; height: 42px; background: #fff1f2; color: #e11d48;",
                                                  },
                                                  h.i({
                                                      class: "bi bi-trash fs-5",
                                                  }),
                                              ),
                                              h.span(
                                                  { class: "small fw-medium" },
                                                  "Item will be skipped from import",
                                              ),
                                          ),
                                ),
                            );
                        }),
                    ),
                ),
            ),
        );
    }

    tryAgain() {
        this.closeForm();
        broker.send($e.product.needs.productForm);
    }

    renderFooter() {
        return v(this.isFailed, (failed) => {
            if (failed.value) {
                return h.button(
                    {
                        class: "btn btn-primary rounded-pill px-4 py-3 fw-bold w-100 text-white border-0 shadow-sm mt-2",
                        style: "background: #6366f1;",
                        listeners: { click: () => this.tryAgain() },
                    },
                    h.i({ class: "bi bi-arrow-clockwise me-1" }),
                    h.span("Try Again"),
                );
            }

            return v(this.pendingApprovals, (approvals) => {
                if (approvals.value.length === 0) {
                    return h.button(
                        {
                            class: "btn btn-light rounded-pill px-4 py-3 fw-bold w-100 border-0",
                            style: "background: #f1f5f9; color: #475569;",
                            onclick: this.method("closeForm"),
                        },
                        "Close",
                    );
                }

                return h.div(
                    { class: "d-flex gap-3 mt-2" },
                    h.button(
                        {
                            class: "btn btn-light rounded-pill px-4 py-3 fw-bold flex-fill border-0",
                            style: "background: #f1f5f9; color: #475569;",
                            onclick: this.method("closeForm"),
                        },
                        "Close",
                    ),
                    h.button(
                        {
                            class: "btn rounded-pill px-4 py-3 fw-bold flex-fill text-white border-0 shadow-sm",
                            style: "background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);",
                            listeners: { click: () => this.submitDecisions() },
                        },
                        h.i({ class: "bi bi-check2-all me-1" }),
                        h.span("Add to Cart"),
                    ),
                );
            }, {$class: 'd-block', class: 'd-block'});
        }, {$class: 'd-block', class: 'd-block'});
    }

    $$userProduct = {
        needs: {
            progressForm: (ed, event) => this.showForm(ed, event),
        },
    };

    cleanUp() {
        super.cleanUp();
        if (this.holder) cleanDispose(this.holder);
    }
}

export function ProgressCanvas(bodyContent) {
    return h.$(
        h.div(
            {
                class: "offcanvas-body p-4 d-flex flex-column",
                style: "flex-grow: 1; overflow-y: auto;",
            },
            bodyContent,
        ),
    );
}

export function ProgressModal(bodyContent, footerContent) {
    return h.$(
        h.div(
            {
                class: "modal-content border-0 shadow-lg",
                style: "border-radius: 24px;",
            },
            h.div(
                { class: "modal-body p-4" },
                bodyContent,
                h.div(
                    { class: "modal-footer border-0 p-0 pt-3" },
                    footerContent,
                ),
            ),
        ),
    );
}

ojs(ProductExtractionProgressForm, ProgressCanvas, ProgressModal);
